import { ENETDOWN } from "constants";
import { Not, System } from "ecsy";
import { Magnet, Consumer, TargetAcquiredThisFrame, Targeting, VisibleRange, ElectricConductor, Dead, RangeType, TargetingSpec } from "../../../shared/components";
import { BehaviorsConstants, CollisionCategory } from "../../../shared/constants";
import { getEntityById, getId } from "../../../shared/entityFactory";
import { entityTrace, getEntityDetails, printSyncTrace } from "../../../shared/entityTrace";
import { GameElement } from "../../../shared/gameElementTypes";
import { ClientAction, TargetChangeReceipt } from "../../../shared/messageTypes";
import { deserializeTargetList } from "../../../shared/synchronization";
import { TargetingSystem } from "../../../shared/systems/targetingSystem";
import { addComponent } from "../../../shared/utils";
import { GameClient } from "../gameClient";

/**Client side of Targeting system, mostly just applies changes from server */
export class ClientTargetingSystem extends TargetingSystem {

	game: GameClient;
	changeEvents = new Array<TargetChangeReceipt>();

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;
	}

	init() {
		super.init();
	}

	execute() {
		super.execute();

		//Clean up last frame target acquires
		while (this.queries.targetAcquiredThisFrame.results.length > 0) {
			this.queries.targetAcquiredThisFrame.results[0].removeComponent(TargetAcquiredThisFrame);
		}

		if (!this.game.inSimulation) {
			//Targeting for electricity, note that this only runs in shop, with serverTargetingSystem running the targeting during simulation
			//because the sync system is not currently up to the task of syncing during shop phase because of the lack of a step count. It would
			//be nice to fix this
			const conductorSpec = new TargetingSpec(RangeType.Circle,
				BehaviorsConstants.ConductionRange,
				// @ts-ignore
				-1, //Can't use collision categories here because they are turned off for the client
				BehaviorsConstants.ConductionRange,
				false, false
			);
			for (const entity of this.queries.conductors.results) {
				const conductor = entity.getComponent(ElectricConductor);
				if (!conductor.accessCharge) continue;
				let results = this.findAllTargets(this.searchCache, entity, conductorSpec, new Set([entity.id]));
				conductor.targets = results;
			}
		}

		//Process target change events on the next frame, because they need to be after creation/destruction events
		for (const event of this.changeEvents) {
			const entity = getEntityById(event.id);
			if (!entity) {
				console.error(`Targeting: Entity not found printing trace. 1`);
				throw new Error(`StateSync: Received targetChange for non-existant entity, should be impossible: Step: ${getStepNumber()} intended entity id ${event.id} type ${GameElement[event.receiverType]}.`);
			}
			entityTrace(entity, `(client) Processing targetChange: Step: ${getStepNumber()} event id ${event.id} specIndex ${event.specIndex}.`);

			const targets = deserializeTargetList(event);

			if (event.specIndex >= 0) {
				const targeting = entity.getMutableComponent(Targeting);
				if (!targeting) {
					console.error(`Targeting component not found for entity ${event.id}. intended type ${GameElement[event.receiverType]}. Entity info:
					${getEntityDetails(entity)}`);
					console.error(`Targeting: Entity not found printing trace. 2`);
				}
				targeting.results[event.specIndex] = targets;
			}
			else if (event.specIndex === -1) {
				const antigen = entity.getComponent(Magnet);
				antigen.targets = targets;
			}
			else if (event.specIndex === -2) {
				const range = entity.getComponent(VisibleRange);
				if (!range) {
					console.error(`VisibleRange component not found for entity ${event.id} intended type ${GameElement[event.receiverType]}. Entity info: 
					${getEntityDetails(entity)}`);
					console.error(`Targeting: Entity not found printing trace. 3`);
				}
				const hadNoTarget = !range.targetFound;
				range.nearest = targets[0] || null;
				range.targetFound = range.nearest !== null;
				if (hadNoTarget && range.nearest !== null) {
					addComponent(entity, TargetAcquiredThisFrame);
				}
			}
			else if (event.specIndex === -3) {
				const consumer = entity.getComponent(Consumer);
				consumer.targets = targets;
			}
			else if (event.specIndex === -4) {
				//TODO Both IF statements are hacky crash mitigation, find cause in electricity stuff!
				if (entity && entity.hasComponent(ElectricConductor)) {
					const conductor = entity.getComponent(ElectricConductor);
					if (conductor) {
						conductor.targets = targets;
					}
				}
				else {
					//NOTE: I suspect this can cause ghost virus issue, keep an eye out for it
					console.warn(`Targeting: Dropped electric conductor targets, entity has no conductor. Step: ${getStepNumber()} entity id ${event.id} type ${GameElement[event.receiverType]} targets ${targets.length}`);
				}
			}
		}
		this.changeEvents = [];
	}
}

ClientTargetingSystem.queries = {
	targetAcquiredThisFrame: { components: [TargetAcquiredThisFrame] },
	conductors: { components: [ElectricConductor, Not(Dead)] },
}	